import { HttpErrorResponse } from '@angular/common/http';

/**
 * What an error handler receives once a Blob body has been unwrapped: the backend's parsed
 * `{ generalMsg, detailedMsg, errorCode }` envelope, or the original error object — with its
 * `error` field swapped for the decoded text when the body was not JSON.
 *
 * Always an object, never a bare string. `ErrorService.error()` logs its first argument as the
 * `error.*` attributes of its Sentry log, so a raw string (potentially a whole HTML page) would
 * arrive there with no status, name or message attached.
 */
export type DecodedHttpError = HttpErrorResponse | Record<string, unknown>;

/**
 * A request made with `responseType: 'blob'` gets a Blob back on failure too, so `error.error`
 * is a Blob rather than the parsed error envelope. `ErrorService` looks for `generalMsg` on it,
 * finds neither that nor `message`, and renders its literal fallback — which is why a 403, a 404
 * and a 500 all read as "An unexpected error occurred. Please try again."
 *
 * This decodes the Blob first and hands the parsed envelope on, so the backend's own message
 * reaches the user.
 *
 * A non-Blob error is passed through SYNCHRONOUSLY, so existing call paths keep their timing.
 * A Blob goes through `FileReader`, which is asynchronous — a spec asserting on the outcome needs
 * `fakeAsync`/`tick()` or `done()`, not a synchronous expectation.
 *
 * A body that is not `application/json`, or that fails to parse, comes back as the original error
 * carrying the decoded text in its `error` field. `ErrorService` then falls back to its default
 * string, which is the correct floor for an HTML proxy error page — not a regression.
 */
export function decodeBlobError(error: HttpErrorResponse, handle: (decoded: DecodedHttpError) => void): void {
  const body = error?.error;
  if (!(body instanceof Blob)) {
    handle(error);
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    const text = reader.result as string;
    // Blob.type carries the full Content-Type including parameters, so an envelope served as
    // 'application/json; charset=utf-8' must still be parsed — match the essence only.
    if (isJson(body.type)) {
      try {
        handle(JSON.parse(text));
        return;
      } catch {
        // Fall through to the decoded text: a body that claims JSON and is not still has to
        // reach the user as something, and ErrorService's default is a better floor than a
        // thrown SyntaxError inside a FileReader callback.
      }
    }
    // Keep the original error and swap only its body, so the handler always gets an object: the
    // transport facts survive into the log, and ErrorService's own string branch still gets a
    // chance at an envelope that was served under the wrong content type.
    handle({ ...error, error: text } as Record<string, unknown>);
  };
  // Nothing was decoded, so the Blob stays in place and ErrorService lands on its default string.
  // The reader's reason rides a sibling field, which is the only record of why the decode failed.
  reader.onerror = () => handle({ ...error, readError: reader.error?.message } as Record<string, unknown>);
  reader.readAsText(body);
}

function isJson(contentType: string): boolean {
  return (contentType || '').split(';')[0].trim().toLowerCase() === 'application/json';
}
